import { getReadModelDesignContract, isSandboxOutputUsable } from '@shared/tool-design-contract';
import { buildReadModelTabs } from '@shared/tool-app-composition';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ComposerChat } from '@/components/composer/ComposerChat';
import { isForgeTurnAlive } from '@shared/forge-turn';
import { usageSheetSignature } from '@shared/forge-usage-sheet';
import type { ForgeUsageSheet, UsageSheetCheck, UsageSheetCheckTarget } from '@shared/types/forge-usage-sheet';
import { UsageSheetCard } from '@/components/composer/UsageSheetCard';
import type { ForgePane } from '@/components/composer/ForgeSteps';
import { toDraftUsageSheet } from '@/lib/usage-sheet-draft';
import { isSheetPreviewFresh, sheetPreviewSignature, type SheetPreview } from '@shared/forge-sheet-preview';
import { SheetPreviewPanel } from '@/components/composer/SheetPreviewPanel';
import {
  readServerTurnVerdict,
  type ServerTurnVerdict,
  usePendingComposerTurn,
} from '@/components/composer/usePendingComposerTurn';
import { ComposerPreview } from '@/components/composer/ComposerPreview';
import { ForgeAppPages, useForgeApp } from '@/components/composer/ForgeAppPages';
import { ForgeStage } from '@/components/composer/ForgeStage';
import { PreviewVersionFrame } from '@/components/composer/PreviewVersionFrame';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import {
  resolveFlowComposerThreadActionOrigin,
  resolveInitialFlowComposerPrompt,
} from '@/lib/flow-composer-handoff';
import { forgeWorkerUrl, invokeForgeWorker } from '@/lib/forge-worker-client';
import { supabase } from '@/lib/supabase';
import { invokeEdgeFunctionStream } from '@/lib/sse-client';
import { buildRunResultTabs, type ToolResultTab } from '@/lib/tool-result-composition';
import { toSafeUiMessage } from '@/lib/dashboard-errors';
import { appDataIdentityProblem, appObjectCoverage, appResultTable } from '@shared/app-data-identity';
import { projectTestApp } from '@/lib/app-preview';
import { ForgeAppPreview, type PreviewNextActions } from '@/components/composer/ForgeAppPreview';
import { cn } from '@/lib/utils';
import { listKnowledgeFolders, type KnowledgeFolderWithCount } from '@/lib/knowledge';
import { getFlowToolReadiness } from '@/lib/tool-surface';
import { buildSupportMailto } from '@/lib/support-contact';
import { invokeExecutionChat } from '@/lib/execution-chat';
import { parseResultPayload } from '@shared/types/result-composition';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';
import type { Domino, Flow } from '@shared/types/flows';
import {
  extractInternalDiagnosisFromError,
  extractProductDiagnosisFromError,
  type InternalDiagnosis,
  type ProductDiagnosis,
  type ProductDiagnosisAction,
} from '@shared/types/product-diagnosis';
import type { AnyViewEnvelope, ViewType } from '@shared/types/views';
import { isViewType } from '@shared/types/views';
import type {
  FlowComposerDraft,
  FlowComposerMessage,
  FlowComposerProgressEvent,
  FlowComposerResources,
  FlowComposerTestBlockedResponse,
  FlowComposerTestStartedResponse,
  FlowComposerVersionsResponse,
  FlowDraftVersionSummary,
} from '@shared/types/flow-composer';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAnyViewEnvelope = (value: unknown): value is AnyViewEnvelope =>
  isRecord(value) &&
  isViewType(value.view_type) &&
  typeof value.schema_version === 'string' &&
  isRecord(value.data);

const getExecutionSummaryNarration = (value: unknown): string | null => {
  if (!isRecord(value) || typeof value.narration !== 'string') return null;
  const narration = value.narration.trim();
  return narration.length > 0 ? narration : null;
};

const buildDraftVersionSignature = (
  nextDraft: FlowComposerDraft | null
): string | null => {
  if (!nextDraft) return null;
  return JSON.stringify({
    title: nextDraft.title,
    description: nextDraft.description,
    inputs: nextDraft.inputs,
    dominos: nextDraft.dominos,
    outputContract: nextDraft.outputContract,
    toolSurface: nextDraft.toolSurface,
    resultComposition: nextDraft.resultComposition,
  });
};

const isMessageArray = (value: unknown): value is FlowComposerMessage[] => {
  if (!Array.isArray(value)) return false;
  return value.every((message) => {
    if (
      !isRecord(message) ||
      typeof message.id !== 'string' ||
      (message.role !== 'user' && message.role !== 'assistant') ||
      typeof message.content !== 'string' ||
      typeof message.createdAt !== 'string'
    ) {
      return false;
    }

    const reasoningSteps = message.reasoningSteps;
    if (reasoningSteps === undefined) return true;
    return (
      Array.isArray(reasoningSteps) &&
      reasoningSteps.every(
        (step) =>
          isRecord(step) &&
          typeof step.id === 'string' &&
          typeof step.label === 'string' &&
          typeof step.summary === 'string'
      )
    );
  });
};

const isPreviewResponse = (
  value: unknown
): value is {
  status: 'preview';
  conversationId: string;
  message: string;
  progress: number;
  draft: FlowComposerDraft;
  resources: FlowComposerResources;
  messages: FlowComposerMessage[];
  sandboxExecutionId?: string | null;
} => {
  if (!isRecord(value)) return false;
  if (value.status !== 'preview') return false;
  return (
    typeof value.conversationId === 'string' &&
    typeof value.message === 'string' &&
    typeof value.progress === 'number' &&
    isRecord(value.draft) &&
    isRecord(value.resources) &&
    isMessageArray(value.messages)
  );
};

const isClarifyingResponse = (
  value: unknown
): value is {
  status: 'clarifying';
  conversationId: string;
  message: string;
  progress: number;
  suggestions: string[];
  messages: FlowComposerMessage[];
} => {
  if (!isRecord(value)) return false;
  if (value.status !== 'clarifying') return false;
  const suggestions = value.suggestions;
  const validSuggestions = Array.isArray(suggestions)
    ? suggestions.every((item) => typeof item === 'string')
    : false;

  return (
    typeof value.conversationId === 'string' &&
    typeof value.message === 'string' &&
    typeof value.progress === 'number' &&
    validSuggestions &&
    isMessageArray(value.messages)
  );
};

const isCreatedResponse = (
  value: unknown
): value is { status: 'created'; flowId?: string; toolAppKey?: string } =>
  isRecord(value) && value.status === 'created' && (typeof value.flowId === 'string' || typeof value.toolAppKey === 'string');

const isTestStartedResponse = (
  value: unknown
): value is FlowComposerTestStartedResponse =>
  isRecord(value) &&
  value.status === 'test_started' &&
  typeof value.conversationId === 'string' &&
  typeof value.sandboxFlowId === 'string' &&
  typeof value.executionId === 'string';

const isTestBlockedResponse = (
  value: unknown
): value is FlowComposerTestBlockedResponse =>
  isRecord(value) &&
  value.status === 'test_blocked' &&
  typeof value.conversationId === 'string' &&
  Array.isArray(value.missingInputs) &&
  value.missingInputs.every(
    (input) =>
      isRecord(input) && typeof input.key === 'string' && typeof input.label === 'string'
  );

const isVersionsResponse = (value: unknown): value is FlowComposerVersionsResponse =>
  isRecord(value) &&
  value.status === 'versions' &&
  typeof value.conversationId === 'string' &&
  Array.isArray(value.versions);

/** Past the server's 2-minute stale threshold, with margin for its own clock. */
const SANDBOX_SILENCE_MS = 150_000;

interface SandboxTestState {
  status: FlowExecutionStatus | 'blocked';
  executionId: string | null;
  sandboxFlowId: string | null;
  missingInputs?: Array<{ key: string; label: string }>;
  diagnosis?: ProductDiagnosis | null;
  /** Technical hint driving repair. Never rendered; only sent to the planner. */
  internalDiagnosis?: InternalDiagnosis | null;
  resultTab?: ToolResultTab | null;
  resultTabs?: ToolResultTab[];
  publishBlocked?: string | null;
  objectCoverage?: string | null;
  /** U1: the test's list table, from which the app's preview is drawn. */
  appTable?: Record<string, unknown> | null;
}

interface ForgeDraftListItem {
  id: string;
  status: 'active' | 'preview';
  title: string;
  description: string | null;
  updatedAt: string;
  messageCount: number;
  flowId: string | null;
  /** The last message is the user's: Domino still answers it, or its answer
   * was interrupted (no worker beat any more). */
  openTurn: 'answering' | 'interrupted' | null;
}

interface ForgeToolListItem {
  id: string;
  title: string;
  description: string | null;
  updatedAt: string;
  status: 'draft' | 'active';
}

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const truncateText = (value: string, maxLength: number): string =>
  value.length > maxLength ? `${value.slice(0, maxLength - 1).trimEnd()}…` : value;

const getFirstUserMessage = (messages: FlowComposerMessage[]): string | null => {
  const firstUserMessage = messages.find((message) => message.role === 'user');
  return firstUserMessage ? readString(firstUserMessage.content) : null;
};

const readOpenTurn = (
  messages: FlowComposerMessage[],
  heartbeatAt: string | null,
): ForgeDraftListItem['openTurn'] => {
  const last = messages[messages.length - 1];
  if (!last || last.role !== 'user') return null;
  return isForgeTurnAlive({ heartbeatAt, openedAt: last.createdAt }) ? 'answering' : 'interrupted';
};

const mapForgeDraftRow = (row: unknown): ForgeDraftListItem | null => {
  if (!isRecord(row)) return null;
  const id = readString(row.id);
  const status = row.status;
  const updatedAt = readString(row.updated_at);
  if (!id || (status !== 'active' && status !== 'preview') || !updatedAt) return null;

  const messages = isMessageArray(row.messages) ? row.messages : [];
  const draftRecord = isRecord(row.draft) ? row.draft : null;
  // A conception without app yet (still being framed, or its first answer in
  // progress) is listed too, so a closed page can be found again.
  if (!draftRecord && messages.length === 0) return null;
  const title =
    readString(draftRecord?.title) ??
    (getFirstUserMessage(messages)
      ? truncateText(getFirstUserMessage(messages) as string, 80)
      : null) ??
    'Brouillon sans titre';
  const description = draftRecord
    ? readString(draftRecord.description) ??
      (getFirstUserMessage(messages)
        ? truncateText(getFirstUserMessage(messages) as string, 140)
        : null)
    : null;
  const flowId = readString(row.flow_id);

  return {
    id,
    status,
    title,
    description,
    updatedAt,
    messageCount: messages.length,
    flowId,
    openTurn: readOpenTurn(messages, readString(row.turn_heartbeat_at)),
  };
};

const mapForgeToolRow = (row: unknown): ForgeToolListItem | null => {
  if (!isRecord(row)) return null;
  const id = readString(row.id);
  const title = readString(row.title);
  const updatedAt = readString(row.updated_at);
  const status = row.status;
  if (!id || !title || !updatedAt || (status !== 'draft' && status !== 'active')) {
    return null;
  }

  return {
    id,
    title,
    description: readString(row.description),
    updatedAt,
    status,
  };
};

const formatDraftUpdatedAt = (isoValue: string): string => {
  const date = new Date(isoValue);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) return 'date inconnue';

  const diffMs = Date.now() - timestamp;
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs >= 0 && diffMs < minuteMs) return 'à l’instant';
  if (diffMs >= 0 && diffMs < hourMs) {
    const minutes = Math.max(1, Math.round(diffMs / minuteMs));
    return `il y a ${minutes} min`;
  }
  if (diffMs >= 0 && diffMs < dayMs) {
    const hours = Math.max(1, Math.round(diffMs / hourMs));
    return `il y a ${hours} h`;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

interface ForgeDraftRecoveryListProps {
  drafts: ForgeDraftListItem[];
  loading: boolean;
  error: string | null;
  onOpenDraft: (conversationId: string) => void;
  onRefresh: () => void;
}

const ForgeDraftRecoveryList = ({
  drafts,
  loading,
  error,
  onOpenDraft,
  onRefresh,
}: ForgeDraftRecoveryListProps) => {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-domino-inkSoft">Conceptions en cours</p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRefresh}
          disabled={loading}
          title="Rafraîchir les conceptions en cours"
          aria-label="Rafraîchir les conceptions en cours"
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      {error ? (
        <p role="alert" className="mt-3 text-sm text-domino-inkSoft">
          {toSafeUiMessage(
            error,
            'Domino ne peut pas charger les conceptions en cours pour le moment.'
          )}
        </p>
      ) : null}

      <div className="mt-2 divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
        {loading && drafts.length === 0 ? (
          <div className="py-3 text-sm text-domino-inkSoft">
            Chargement des conceptions en cours…
          </div>
        ) : null}

        {!loading && drafts.length === 0 && !error ? (
          <div className="py-3 text-sm text-domino-inkSoft">Aucune conception en cours.</div>
        ) : null}

        {drafts.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpenDraft(item.id)}
            className="group w-full px-1 py-3 text-left transition hover:bg-domino-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-domino-prune"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-domino-ink">
                  {item.title}
                </p>
                {item.description ? (
                  <p className="mt-1 line-clamp-1 text-xs leading-5 text-domino-inkSoft">
                    {item.description}
                  </p>
                ) : null}
                <p className="mt-1 text-[11px] text-domino-inkSoft">
                  {item.openTurn === 'answering' ? 'Réponse en cours · ' : ''}
                  {item.openTurn === 'interrupted' ? 'Réponse interrompue · ' : ''}
                  {formatDraftUpdatedAt(item.updatedAt)}
                </p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-domino-inkSoft transition group-hover:translate-x-0.5 group-hover:text-domino-aubergine" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

interface ForgeToolAdminListProps {
  tools: ForgeToolListItem[];
  loading: boolean;
  error: string | null;
  onOpenTool: (flowId: string) => void;
  onOpenApplication: (flowId: string) => void;
  onActivateTool: (flowId: string) => void;
  activatingToolId: string | null;
  onRefresh: () => void;
}

const ForgeToolAdminList = ({
  tools,
  loading,
  error,
  onOpenTool,
  onOpenApplication,
  onActivateTool,
  activatingToolId,
  onRefresh,
}: ForgeToolAdminListProps) => (
  <div>
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm font-medium text-domino-inkSoft">Outils créés</p>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRefresh}
        disabled={loading}
        title="Rafraîchir les outils créés"
        aria-label="Rafraîchir les outils créés"
      >
        <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
      </Button>
    </div>

    {error ? (
      <p role="alert" className="mt-3 text-sm text-domino-inkSoft">
        {toSafeUiMessage(error, 'Domino ne peut pas charger les outils créés pour le moment.')}
      </p>
    ) : null}

    <div className="mt-2 divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
      {loading && tools.length === 0 ? (
        <div className="py-3 text-sm text-domino-inkSoft">Chargement des outils…</div>
      ) : null}

      {!loading && tools.length === 0 && !error ? (
        <div className="py-3 text-sm text-domino-inkSoft">Aucun outil créé.</div>
      ) : null}

      {tools.map((item) => (
        <div
          key={item.id}
          className="flex flex-col items-stretch gap-2 py-3 sm:flex-row sm:items-center sm:gap-3"
        >
          <button
            type="button"
            onClick={() => onOpenTool(item.id)}
            aria-label={`Modifier ${item.title}`}
            className="group min-w-0 flex-1 px-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-domino-ink">{item.title}</p>
                {item.description ? (
                  <p className="mt-1 line-clamp-1 text-xs leading-5 text-domino-inkSoft">
                    {item.description}
                  </p>
                ) : null}
                <p className="mt-1 text-[11px] text-domino-inkSoft">
                  {formatDraftUpdatedAt(item.updatedAt)}
                </p>
              </div>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-domino-inkSoft transition group-hover:translate-x-0.5 group-hover:text-domino-aubergine" />
            </div>
          </button>
          {item.status === 'active' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => onOpenApplication(item.id)}
            >
              Ouvrir
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => onActivateTool(item.id)}
              loading={activatingToolId === item.id}
              disabled={activatingToolId !== null}
            >
              Ajouter aux Applications
            </Button>
          )}
        </div>
      ))}
    </div>
  </div>
);

const isProgressEvent = (value: unknown): value is FlowComposerProgressEvent =>
  isRecord(value) &&
  (value.stage === 'context' ||
    value.stage === 'planning' ||
    value.stage === 'reasoning' ||
    value.stage === 'validation' ||
    value.stage === 'preview' ||
    value.stage === 'saving') &&
  typeof value.label === 'string' &&
  (value.progress === undefined || typeof value.progress === 'number') &&
  (value.detail === undefined || typeof value.detail === 'string');

/** The connection dropped mid-turn; the server-side turn is not affected. */
class ComposerConnectionLostError extends Error {
  constructor() {
    super('La connexion a été coupée pendant que Domino travaillait.');
    this.name = 'ComposerConnectionLostError';
  }
}

/** The user stopped the turn (« Arrêter »): the stop request applies the
 * persisted conversation, so the stream's end is not an error. */
class ComposerTurnStoppedError extends Error {
  constructor() {
    super('Réponse arrêtée à votre demande.');
    this.name = 'ComposerTurnStoppedError';
  }
}

export const FlowComposerPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversationId, setConversationId] = useState<string | null>(null);
  // Known as soon as the server created the conversation, before any AI call
  // (decision Forge FDE, F0): read by stream callbacks that outlive a render.
  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conversationId;
  const [serverTurn, setServerTurn] = useState<ServerTurnVerdict | undefined>(undefined);
  // Usage sheet of the framing (decision Forge FDE, F1) and whether this exact
  // sheet was validated and the tool built from it.
  const [usageSheet, setUsageSheet] = useState<ForgeUsageSheet | null>(null);
  const [usageSheetValidated, setUsageSheetValidated] = useState(false);
  // The sheet as Domino writes it during a framing turn (display only).
  const [usageSheetDraft, setUsageSheetDraft] = useState<ForgeUsageSheet | null>(null);
  // Live checks of the running framing turn (F2): pending reads, then results.
  const [liveChecks, setLiveChecks] = useState<{ pending: UsageSheetCheckTarget[]; checks: UsageSheetCheck[] } | null>(null);
  // Example result screen drawn from the sheet (F3), redrawn when the promised result changes.
  const [usageSheetPreview, setUsageSheetPreview] = useState<SheetPreview | null>(null);
  const [sheetPreviewLoading, setSheetPreviewLoading] = useState(false);
  const [sheetPreviewError, setSheetPreviewError] = useState<string | null>(null);
  const sheetPreviewRequestRef = useRef<string | null>(null);
  const [sheetPreviewRetry, setSheetPreviewRetry] = useState(0);
  const [stopping, setStopping] = useState(false);
  const stoppingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [messages, setMessages] = useState<FlowComposerMessage[]>([]);
  const [draft, setDraft] = useState<FlowComposerDraft | null>(null);
  const [resources, setResources] = useState<FlowComposerResources | null>(null);

  const [loading, setLoading] = useState(false);
  const [improving, setImproving] = useState(false);
  /** Why creating or restoring the tool was refused: shown next to its buttons, not only in the chat. */
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const [validatingDesign, setValidatingDesign] = useState(false);
  const [chatDraftRequest, setChatDraftRequest] = useState<{ text: string; key: number } | null>(null);
  /** Places a proposed message in the chat field for the user to edit and send. */
  const handleDraftMessage = (text: string) => {
    setChatDraftRequest({ text, key: Date.now() });
    setMobilePane('chat');
  };
  const [sandboxTest, setSandboxTest] = useState<SandboxTestState | null>(null);
  const [versions, setVersions] = useState<FlowDraftVersionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<'chat' | 'preview'>('chat');
  /** null follows the progression: the Forge opens the step where the work is (1 Fiche, 2 Test, 3 App). */
  const [forgePane, setForgePane] = useState<ForgePane | null>(null);
  const [streamProgress, setStreamProgress] = useState<FlowComposerProgressEvent | null>(
    null
  );
  const [availableFolders, setAvailableFolders] = useState<KnowledgeFolderWithCount[]>(
    []
  );
  const [extraKnowledgeFolderIds, setExtraKnowledgeFolderIds] = useState<string[]>([]);
  const [forgeDrafts, setForgeDrafts] = useState<ForgeDraftListItem[]>([]);
  const [forgeDraftsLoading, setForgeDraftsLoading] = useState(false);
  const [forgeDraftsError, setForgeDraftsError] = useState<string | null>(null);
  const [forgeTools, setForgeTools] = useState<ForgeToolListItem[]>([]);
  const [forgeToolsLoading, setForgeToolsLoading] = useState(false);
  const [forgeToolsError, setForgeToolsError] = useState<string | null>(null);
  const [activatingToolId, setActivatingToolId] = useState<string | null>(null);

  const requestedConversationId = searchParams.get('conversationId');
  const requestedToolAppKey = searchParams.get('toolAppKey');
  const requestedFlowId = searchParams.get('flowId');
  const forgeApp = useForgeApp(requestedFlowId, requestedConversationId);
  const requestedRepairRunId = searchParams.get('repairRun');
  const requestedInitialPrompt = useMemo(
    () => resolveInitialFlowComposerPrompt(searchParams, location.state),
    [location.state, searchParams]
  );
  const requestedThreadActionOrigin = useMemo(
    () => resolveFlowComposerThreadActionOrigin(location.state),
    [location.state]
  );
  const requestedRepairPrompt = useMemo(() => {
    if (!requestedRepairRunId) return null;
    if (!isRecord(location.state) || typeof location.state.repairPrompt !== 'string') {
      return null;
    }
    const trimmed = location.state.repairPrompt.trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [location.state, requestedRepairRunId]);
  const startedPromptRef = useRef<string | null>(null);
  const startedRepairRef = useRef<string | null>(null);
  // Durable sandbox test restore: a preview response carries the test run that
  // is still valid for the draft it returns. We stash it keyed by the draft's
  // signature so the draft-change effect restores it (resume/reload) instead of
  // clearing it, while still clearing on a genuine draft change.
  const sandboxRestoreRef = useRef<{ executionId: string; signature: string } | null>(
    null
  );

  // Load the user's accessible knowledge folders once so the right panel can show a picker.
  useEffect(() => {
    let cancelled = false;
    void listKnowledgeFolders()
      .then((folders) => {
        if (!cancelled) setAvailableFolders(folders);
      })
      .catch(() => {
        // Non-blocking; the section will just show "Aucun dossier".
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasPreview = Boolean(draft && resources);
  const showInitialState = messages.length === 0 && !loading;
  const shouldShowDraftRecovery =
    !requestedConversationId &&
    !requestedFlowId &&
    !requestedInitialPrompt &&
    !conversationId &&
    messages.length === 0;
  const chatPlaceholder = useMemo(() => {
    if (!conversationId) {
      return 'Décrivez l’application ou l’outil à créer…';
    }
    return 'Ajoute une précision ou réponds à la question...';
  }, [conversationId]);

  const assistantContext = useMemo(
    () => ({
      surface: 'forge' as const,
      routePath: '/forge',
      routeLabel: 'Forge',
      visibleSummary: [
        'Surface de fabrication d’un nouvel outil métier Domino.',
        `Conversation de création : ${messages.length} message${messages.length > 1 ? 's' : ''}.`,
        hasPreview
          ? 'Un prototype de tool est affiché avec un mode aperçu et un mode paramétrage.'
          : 'Domino collecte encore le signal, les étapes et le livrable attendu.',
      ].join(' '),
      visibleEntities: [
        isRecord(draft) && typeof draft.title === 'string' ? draft.title : null,
      ].filter((value): value is string => Boolean(value)),
      visibleError: error,
      selectedFlowId: requestedFlowId,
      forgeConversationId: conversationId,
      draftTitle: isRecord(draft) && typeof draft.title === 'string' ? draft.title : null,
    }),
    [conversationId, draft, error, hasPreview, messages.length, requestedFlowId]
  );

  useRegisterAppAssistantContext(assistantContext);

  const syncConversationQuery = (id: string | null) => {
    const nextParams = new URLSearchParams();
    const queryFlowId = searchParams.get('flowId');
    if (queryFlowId) nextParams.set('flowId', queryFlowId);
    if (!id) {
      setSearchParams(nextParams, { replace: true });
      return;
    }
    nextParams.set('conversationId', id);
    setSearchParams(nextParams, { replace: true });
  };

  const resetComposerState = (nextConversationId: string | null = null) => {
    sandboxRestoreRef.current = null;
    setUsageSheet(null);
    setUsageSheetValidated(false);
    setUsageSheetPreview(null);
    setSheetPreviewError(null);
    sheetPreviewRequestRef.current = null;
    setConversationId(nextConversationId);
    setMessages([]);
    setDraft(null);
    setResources(null);
    setExtraKnowledgeFolderIds([]);
    setVersions([]);
    setMobilePane('chat');
    setForgePane(null);
    setStreamProgress(null);
  };

  const loadForgeDrafts = useCallback(async () => {
    setForgeDraftsLoading(true);
    setForgeDraftsError(null);
    try {
      const { data, error: draftsError } = await supabase
        .from('flow_conversations')
        .select('id,status,messages,draft,flow_id,updated_at,turn_heartbeat_at')
        .in('status', ['active', 'preview'])
        .order('updated_at', { ascending: false })
        .limit(12);

      if (draftsError) {
        throw new Error(draftsError.message);
      }

      const seenEditedFlowIds = new Set<string>();
      const nextDrafts: ForgeDraftListItem[] = [];
      for (const item of (data ?? []).map(mapForgeDraftRow)) {
        if (!item) continue;
        if (item.flowId) {
          if (seenEditedFlowIds.has(item.flowId)) continue;
          seenEditedFlowIds.add(item.flowId);
        }
        nextDrafts.push(item);
        if (nextDrafts.length >= 8) break;
      }

      setForgeDrafts(nextDrafts);
    } catch (draftsError) {
      setForgeDraftsError(
        draftsError instanceof Error
          ? draftsError.message
          : 'Impossible de charger les brouillons Forge'
      );
    } finally {
      setForgeDraftsLoading(false);
    }
  }, []);

  const loadForgeTools = useCallback(async () => {
    setForgeToolsLoading(true);
    setForgeToolsError(null);
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError || !authData.user) {
        throw new Error(authError?.message ?? 'Utilisateur non authentifié');
      }

      const { data, error: toolsError } = await supabase
        .from('flows')
        .select('id,title,description,status,updated_at')
        .eq('user_id', authData.user.id)
        .eq('source_mode', 'composer')
        .eq('is_sandbox', false)
        .in('status', ['draft', 'active'])
        .order('updated_at', { ascending: false })
        .limit(12);

      if (toolsError) throw new Error(toolsError.message);
      setForgeTools((data ?? []).map(mapForgeToolRow).filter((item): item is ForgeToolListItem => item !== null));
    } catch (toolsError) {
      setForgeToolsError(
        toolsError instanceof Error ? toolsError.message : 'Impossible de charger les outils Forge'
      );
    } finally {
      setForgeToolsLoading(false);
    }
  }, []);

  const handleActivateForgeTool = async (flowId: string) => {
    setActivatingToolId(flowId);
    setForgeToolsError(null);
    try {
      const [flowResult, dominosResult] = await Promise.all([
        supabase.from('flows').select('*').eq('id', flowId).single(),
        supabase.from('dominos').select('*').eq('flow_id', flowId).order('position'),
      ]);

      if (flowResult.error || !flowResult.data || dominosResult.error) {
        throw new Error('Domino ne peut pas vérifier cet outil pour le moment.');
      }

      const readiness = getFlowToolReadiness(
        flowResult.data as Flow,
        (dominosResult.data ?? []) as Domino[]
      );
      if (!readiness.publishable) {
        throw new Error(
          `Cet outil doit encore être ajusté dans la Forge : ${readiness.blockingMessages.join(' ')}`
        );
      }

      const { data: activatedFlow, error: activationError } = await supabase
        .from('flows')
        .update({ status: 'active' })
        .eq('id', flowId)
        .select('id')
        .single();
      if (activationError || !activatedFlow) {
        throw new Error(activationError?.message ?? 'Impossible d’ajouter cet outil aux Applications.');
      }

      setForgeTools((current) =>
        current.map((tool) => (tool.id === flowId ? { ...tool, status: 'active' } : tool))
      );
    } catch (activationError) {
      setForgeToolsError(
        activationError instanceof Error
          ? activationError.message
          : 'Impossible d’ajouter cet outil aux Applications.'
      );
    } finally {
      setActivatingToolId(null);
    }
  };

  const handleOpenForgeDraft = (nextConversationId: string) => {
    setError(null);
    resetComposerState(nextConversationId);
    syncConversationQuery(nextConversationId);
  };

  useEffect(() => {
    if (!shouldShowDraftRecovery) return;
    void loadForgeDrafts();
    void loadForgeTools();
  }, [shouldShowDraftRecovery, loadForgeDrafts, loadForgeTools]);

  const applyResponse = (payload: unknown): { flowId?: string; toolAppKey?: string } => {
    setServerTurn(readServerTurnVerdict(payload));
    setUsageSheetDraft(null);
    if (isRecord(payload) && (payload.status === 'preview' || payload.status === 'clarifying')) {
      // Validated server-side before it is sent.
      setUsageSheet(isRecord(payload.usageSheet) ? payload.usageSheet as unknown as ForgeUsageSheet : null);
      setUsageSheetValidated(payload.usageSheetValidated === true);
      setUsageSheetPreview(isRecord(payload.usageSheetPreview) ? payload.usageSheetPreview as unknown as SheetPreview : null);
    }
    if (isCreatedResponse(payload)) {
      return { flowId: payload.flowId, toolAppKey: payload.toolAppKey };
    }

    if (isPreviewResponse(payload)) {
      // Draft versions are recorded server-side and reloaded by the version
      // effect (keyed on the draft signature), so there is no local stack here.
      // Stash the durable sandbox test pointer keyed by this draft's signature
      // so the draft-change effect restores it (resume/reload) rather than
      // clearing it. The backend already nulled it on a real edit.
      const restoreSignature = buildDraftVersionSignature(payload.draft);
      sandboxRestoreRef.current =
        typeof payload.sandboxExecutionId === 'string' && restoreSignature
          ? {
              executionId: payload.sandboxExecutionId,
              signature: restoreSignature,
            }
          : null;
      setConversationId(payload.conversationId);
      syncConversationQuery(payload.conversationId);
      setMessages(payload.messages);
      setDraft(payload.draft);
      setResources(payload.resources);
      setMobilePane('preview');
      setForgePane(null);
      return {};
    }

    if (isClarifyingResponse(payload)) {
      setConversationId(payload.conversationId);
      syncConversationQuery(payload.conversationId);
      setMessages(payload.messages);
      // A question keeps the app already shown: the backend keeps the stored
      // draft, and the next preview replaces it in place.
      setMobilePane('chat');
      return {};
    }

    if (isRecord(payload) && typeof payload.error === 'string') {
      throw new Error(payload.error);
    }

    throw new Error('Réponse invalide du serveur.');
  };

  const invokeComposer = async (body: Record<string, unknown>): Promise<unknown> => {
    if (forgeWorkerUrl) {
      return await invokeForgeWorker(body);
    }

    throw new Error(
      'Forge worker non configuré. Définis VITE_FORGE_WORKER_URL pour utiliser la Forge hors Supabase Edge.'
    );
  };

  const buildSandboxInputValues = (
    nextDraft: FlowComposerDraft
  ): Record<string, unknown> =>
    nextDraft.inputs.reduce<Record<string, unknown>>((accumulator, input) => {
      const value = input.defaultValue ?? input.options?.[0]?.value;
      if (typeof value === 'string' && value.trim().length > 0) {
        accumulator[input.key] = value;
      }
      return accumulator;
    }, {});

  const draftSignature = useMemo(() => {
    return buildDraftVersionSignature(draft);
  }, [draft]);

  useEffect(() => {
    if (!draft?.readModel || !conversationId) return;
    let canceled = false;
    void supabase.from('tool_design_tests').select('id,result_payload,succeeded')
      .eq('conversation_id', conversationId).eq('design_contract', getReadModelDesignContract(draft))
      .order('created_at', { ascending: false }).limit(1).maybeSingle()
      .then(({ data, error: testError }) => {
        if (canceled) return;
        if (testError) { setError('Impossible de reprendre la vérification des sources.'); return; }
        const result = parseResultPayload(data?.result_payload);
        if (data && result && draft.readModel) setSandboxTest({
          status: data.succeeded ? 'completed' : 'failed', executionId: data.id, sandboxFlowId: null,
          resultTabs: buildReadModelTabs(draft.readModel, result),
        });
      });
    return () => { canceled = true; };
  }, [conversationId, draftSignature]);

  // A draft change invalidates any in-memory sandbox test, EXCEPT when the
  // backend told us (via the preview response) that a test run is still valid
  // for exactly this draft — then restore it so the result and the publish gate
  // survive leaving the Forge / reloading. The poll effect reconciles status.
  useEffect(() => {
    const restore = sandboxRestoreRef.current;
    if (restore && restore.signature === draftSignature) {
      setSandboxTest((current) =>
        current?.executionId === restore.executionId
          ? current
          : { status: 'running', executionId: restore.executionId, sandboxFlowId: null }
      );
      return;
    }
    setSandboxTest(null);
  }, [draftSignature]);

  // Durable version history: reload the list whenever the conversation or its
  // draft changes (the backend records a version on every draft change). This
  // makes "Version précédente" survive a page reload. Best-effort.
  useEffect(() => {
    if (!conversationId || !draftSignature) return;
    let canceled = false;
    (async () => {
      try {
        const payload = await invokeComposer({
          action: 'list_versions',
          conversationId,
        });
        if (!canceled && isVersionsResponse(payload)) {
          setVersions(payload.versions);
        }
      } catch {
        // Version history is non-critical; ignore load failures.
      }
    })();
    return () => {
      canceled = true;
    };
  }, [conversationId, draftSignature]);

  /**
   * Streams a planner-driven composer action (start / answer / revise). Inserts an
   * "in-progress" assistant message into the chat, appends the streamed deltas to it
   * live, then replaces it with the canonical persisted message list when the server
   * emits its `complete` event. Returns the resolved payload (same shape as the
   * non-streaming call) so callers can chain navigation, etc.
   */
  const streamComposer = async (body: Record<string, unknown>): Promise<unknown> => {
    const liveAssistantId = `live-assistant-${Date.now()}`;
    // The bubble shows a real backend phase, or "Domino réfléchit" until one arrives.
    setStreamProgress(null);
    setMessages((current) => [
      ...current,
      {
        id: liveAssistantId,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
      },
    ]);

    let resolvedPayload: unknown = null;
    let resolvedError: Error | null = null;

    try {
      if (!forgeWorkerUrl) {
        throw new Error(
          'Forge worker non configuré. Définis VITE_FORGE_WORKER_URL pour utiliser la Forge hors Supabase Edge.'
        );
      }
      await invokeEdgeFunctionStream({
        url: forgeWorkerUrl,
        body,
        onEvent: (eventName, data) => {
          if (eventName === 'delta' && isRecord(data) && typeof data.delta === 'string') {
            const fragment = data.delta;
            setMessages((current) =>
              current.map((message) =>
                message.id === liveAssistantId
                  ? { ...message, content: `${message.content}${fragment}` }
                  : message
              )
            );
            return;
          }
          if (eventName === 'progress' && isProgressEvent(data)) {
            setStreamProgress(data);
            return;
          }
          if (eventName === 'checks' && isRecord(data)) {
            setLiveChecks({
              pending: Array.isArray(data.pending) ? data.pending as UsageSheetCheckTarget[] : [],
              checks: Array.isArray(data.checks) ? data.checks as UsageSheetCheck[] : [],
            });
            return;
          }
          if (eventName === 'usage_sheet' && isRecord(data)) {
            setUsageSheetDraft(toDraftUsageSheet(data.sheet));
            return;
          }
          if (eventName === 'conversation' && isRecord(data) && typeof data.conversationId === 'string') {
            // The page can now be closed and reopened on this conversation.
            conversationIdRef.current = data.conversationId;
            setConversationId(data.conversationId);
            syncConversationQuery(data.conversationId);
            return;
          }
          if (eventName === 'complete') {
            resolvedPayload = data;
            return;
          }
          if (eventName === 'error') {
            if (isRecord(data) && data.code === 'TURN_CANCELLED') {
              resolvedError = new ComposerTurnStoppedError();
              return;
            }
            const message =
              isRecord(data) && typeof data.message === 'string'
                ? data.message
                : 'Erreur inattendue';
            resolvedError = new Error(message);
            return;
          }
        },
      });
    } catch (streamError) {
      // fetch and stream reads reject with a TypeError when the connection itself
      // drops (Wi-Fi, proxy, VPN); the server keeps working on the turn.
      resolvedError = streamError instanceof TypeError
        ? new ComposerConnectionLostError()
        : streamError instanceof Error
        ? streamError
        : new Error('Erreur de communication avec le serveur.');
    }

    setUsageSheetDraft(null);
    setLiveChecks(null);
    if (resolvedError) {
      // Drop the optimistic live message; the caller's catch block will surface the error.
      setMessages((current) => current.filter((m) => m.id !== liveAssistantId));
      throw resolvedError;
    }
    if (!resolvedPayload) {
      setMessages((current) => current.filter((m) => m.id !== liveAssistantId));
      throw new Error('Le serveur a interrompu la conversation prématurément.');
    }
    // applyResponse replaces the optimistic messages with the persisted conversation.
    return resolvedPayload;
  };

  /** One chat turn: the user's words appear at once, the answer streams. */
  const sendTurn = async (text: string, body: Record<string, unknown>) => {
    setError(null);
    setStreamProgress(null);
    // Optimistic: show the user message immediately so the chat feels conversational.
    // The server response replaces this list with the canonical persisted messages
    // (same content + server-assigned id), so there's no visible flicker.
    const optimisticMessage: FlowComposerMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimisticMessage]);
    setLoading(true);

    try {
      const payload = await streamComposer(body);
      applyResponse(payload);
    } catch (sendError) {
      if (sendError instanceof ComposerTurnStoppedError || stoppingRef.current) {
        // The stop request applies the persisted conversation (user message kept).
        return;
      }
      if (sendError instanceof ComposerConnectionLostError && conversationIdRef.current) {
        // The server persisted the message before any model call and keeps
        // working: keep it, and the pending-turn poll picks up the answer.
        setError(null);
        return;
      }
      // Rollback the optimistic message so the user can re-edit / re-try.
      setMessages((current) => current.filter((m) => m.id !== optimisticMessage.id));
      setError(sendError instanceof ComposerConnectionLostError
        ? 'La connexion a été coupée. Votre demande a peut-être été reçue : rechargez la Forge dans un instant avant de la renvoyer.'
        : sendError instanceof Error ? sendError.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
      setStreamProgress(null);
    }
  };

  const handleSend = (text: string) => sendTurn(text, conversationId
    ? { action: 'answer', conversationId, message: text }
    : {
        action: 'start',
        prompt: text,
        ...(requestedThreadActionOrigin ? { threadActionOrigin: requestedThreadActionOrigin } : {}),
      });

  // Direct edits of the sheet (F1) are saved in order, without AI: the screen
  // shows each edit at once and the server's copy follows.
  const sheetEditsRef = useRef<Promise<void>>(Promise.resolve());
  const handleEditSheet = (next: ForgeUsageSheet) => {
    const id = conversationIdRef.current;
    if (!id) return;
    const previous = usageSheet;
    setUsageSheet(next);
    setUsageSheetValidated(false);
    sheetEditsRef.current = sheetEditsRef.current.then(async () => {
      try {
        // Only the sheet changed: no pane switch, no message reload.
        const payload = await invokeComposer({ action: 'edit_sheet', conversationId: id, sheet: next });
        if (isRecord(payload) && isRecord(payload.usageSheet)) {
          setUsageSheet(payload.usageSheet as unknown as ForgeUsageSheet);
          setUsageSheetValidated(payload.usageSheetValidated === true);
          setUsageSheetPreview(isRecord(payload.usageSheetPreview) ? payload.usageSheetPreview as unknown as SheetPreview : null);
        }
      } catch (editError) {
        setUsageSheet(previous);
        setError(editError instanceof Error ? editError.message : 'Cette modification n’a pas été enregistrée.');
      }
    });
  };

  // Draw the example screen once the sheet is written, after pending edits are
  // saved, and only for a promised result not drawn yet.
  const sheetPreviewKey = usageSheet && !usageSheetDraft ? sheetPreviewSignature(usageSheet) : null;
  useEffect(() => {
    const id = conversationIdRef.current;
    if (!id || !usageSheet || !sheetPreviewKey || isSheetPreviewFresh(usageSheet, usageSheetPreview)) return;
    if (sheetPreviewRequestRef.current === sheetPreviewKey) return;
    sheetPreviewRequestRef.current = sheetPreviewKey;
    setSheetPreviewLoading(true);
    setSheetPreviewError(null);
    void sheetEditsRef.current
      .then(() => invokeComposer({ action: 'preview_sheet', conversationId: id }))
      .then((payload) => {
        if (!mountedRef.current || conversationIdRef.current !== id) return;
        if (isRecord(payload) && isRecord(payload.usageSheetPreview)) {
          setUsageSheetPreview(payload.usageSheetPreview as unknown as SheetPreview);
        }
      })
      .catch((previewError: unknown) => {
        if (!mountedRef.current) return;
        console.warn('forge_sheet_preview_failed', previewError);
        setSheetPreviewError('L’aperçu n’a pas pu être dessiné.');
      })
      .finally(() => {
        if (mountedRef.current) setSheetPreviewLoading(false);
      });
  }, [sheetPreviewKey, usageSheetPreview, sheetPreviewRetry]);

  const retrySheetPreview = () => {
    sheetPreviewRequestRef.current = null;
    setSheetPreviewError(null);
    setSheetPreviewRetry((count) => count + 1);
  };

  // U1: once the test is done, the app filled with its objects, and Domino's
  // next actions on the first ones (planned once per test, nothing written).
  const testedApp = useMemo(
    () => (sandboxTest?.status === 'completed' ? projectTestApp(draft?.appData, sandboxTest.appTable) : null),
    [draft?.appData, sandboxTest?.status, sandboxTest?.appTable],
  );
  const testedExecutionId = testedApp ? sandboxTest?.executionId ?? null : null;
  const [previewActions, setPreviewActions] = useState<{ executionId: string; value: PreviewNextActions | 'loading' | null } | null>(null);
  useEffect(() => {
    const id = conversationIdRef.current;
    if (!id || !testedExecutionId || previewActions?.executionId === testedExecutionId) return;
    setPreviewActions({ executionId: testedExecutionId, value: 'loading' });
    void invokeComposer({ action: 'preview_next_actions', conversationId: id, sandboxExecutionId: testedExecutionId })
      .then((payload) => {
        const objects = isRecord(payload) && Array.isArray(payload.objects) ? payload.objects.filter(isRecord) : [];
        const value: PreviewNextActions = Object.fromEntries(objects.map((object) => [String(object.rowId),
          Array.isArray(object.actions) ? object.actions.filter(isRecord).map((action) => ({
            title: String(action.title ?? ''), whyNow: typeof action.whyNow === 'string' ? action.whyNow : null,
          })).filter((action) => action.title) : null]));
        if (mountedRef.current) setPreviewActions((current) => current?.executionId === testedExecutionId ? { executionId: testedExecutionId, value } : current);
      })
      .catch((previewError: unknown) => {
        console.warn('forge_preview_next_actions_failed', previewError);
        if (mountedRef.current) setPreviewActions((current) => current?.executionId === testedExecutionId ? { executionId: testedExecutionId, value: null } : current);
      });
  }, [testedExecutionId, previewActions?.executionId]);
  const appPreview = testedApp && testedExecutionId ? (
    <ForgeAppPreview key={testedExecutionId} collection={testedApp.collection} records={testedApp.records}
      nextActions={previewActions?.executionId === testedExecutionId ? previewActions.value : null} />
  ) : undefined;

  const sheetPreviewPanel = usageSheet && (usageSheetPreview || sheetPreviewLoading || sheetPreviewError) ? (
    <SheetPreviewPanel
      preview={usageSheetPreview}
      loading={sheetPreviewLoading}
      error={sheetPreviewError}
      onRetry={retrySheetPreview}
    />
  ) : null;

  /** « Valider la fiche » (F1): validates the exact sheet shown, then the
   * server builds the tool from it in the same turn. */
  const handleValidateSheet = () => {
    if (!conversationId || !usageSheet) return;
    void sendTurn('Je valide la fiche : construis l’outil.', {
      action: 'validate_sheet',
      conversationId,
      signature: usageSheetSignature(usageSheet),
    });
  };

  /** Explicit « Arrêter »: the server cancels the model call and closes the
   * turn; the user's message stays in the conversation. */
  const handleStop = async () => {
    const id = conversationIdRef.current;
    if (!id || stoppingRef.current) return;
    stoppingRef.current = true;
    setStopping(true);
    setError(null);
    try {
      applyResponse(await invokeComposer({ action: 'cancel_turn', conversationId: id }));
    } catch (stopError) {
      setError(stopError instanceof Error ? stopError.message : 'Domino n’a pas pu arrêter cette réponse.');
    } finally {
      stoppingRef.current = false;
      setStopping(false);
    }
  };

  const handleConfirm = async () => {
    if (!conversationId) return;
    // Publish gate: require a sandbox test that passed for the current draft.
    // sandboxTest resets on any draft edit, so a 'completed' status here always
    // refers to the draft about to be published.
    if (sandboxTest?.status !== 'completed' || !sandboxTest.executionId) {
      setError("Lance un test du tool et attends qu'il réussisse avant de le publier.");
      return;
    }
    const sandboxExecutionId = sandboxTest.executionId;
    setError(null);
    setPreviewError(null);
    setCreating(true);

    try {
      const payload = await invokeComposer({
        action: 'confirm',
        conversationId,
        sandboxExecutionId,
        ...(draft ? { draft } : {}),
        ...(extraKnowledgeFolderIds.length > 0 ? { extraKnowledgeFolderIds } : {}),
      });

      const next = applyResponse(payload);
      if (next.toolAppKey) navigate(`/apps/${next.toolAppKey}`);
      if (next.flowId) {
        navigate(`/tools/${next.flowId}`);
      }
    } catch (confirmError) {
      setPreviewError(
        toSafeUiMessage(confirmError instanceof Error ? confirmError.message : '', 'L’outil n’a pas pu être créé. Réessayez.')
      );
    } finally {
      setCreating(false);
    }
  };

  const handleTestDraft = async (
    draftOverride?: FlowComposerDraft,
    inputOverrides?: Record<string, unknown>
  ) => {
    // After a repair the new draft is in the returned payload; React state has
    // not updated yet, so callers pass it explicitly to test the fresh version.
    const draftToTest = draftOverride ?? draft;
    if (!conversationId || !draftToTest) return;
    setError(null);
    setTesting(true);
    setSandboxTest(null);

    try {
      const payload = await invokeComposer({
        action: 'test_draft',
        conversationId,
        draft: draftToTest,
        inputValues: {
          ...buildSandboxInputValues(draftToTest),
          ...(inputOverrides ?? {}),
        },
        ...(extraKnowledgeFolderIds.length > 0 ? { extraKnowledgeFolderIds } : {}),
        clientContext: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
        },
      });

      if (isRecord(payload) && payload.status === 'read_model_tested' && typeof payload.testId === 'string' && draftToTest.readModel) {
        const result = parseResultPayload(payload.resultPayload);
        if (!result) throw new Error('Résultat de vérification invalide.');
        setSandboxTest({ status: result.status === 'complete' ? 'completed' : 'failed', executionId: payload.testId, sandboxFlowId: null, resultTabs: buildReadModelTabs(draftToTest.readModel, result) });
        return;
      }
      if (isTestBlockedResponse(payload)) {
        setSandboxTest({
          status: 'blocked',
          executionId: null,
          sandboxFlowId: null,
          missingInputs: payload.missingInputs,
        });
        return;
      }

      if (!isTestStartedResponse(payload)) {
        throw new Error('Réponse de test invalide.');
      }

      setSandboxTest({
        status: 'running',
        executionId: payload.executionId,
        sandboxFlowId: payload.sandboxFlowId,
      });
    } catch (testError) {
      setError(testError instanceof Error ? testError.message : 'Erreur inattendue');
    } finally {
      setTesting(false);
    }
  };

  const handleRestorePreviousDraft = async () => {
    // versions[0] is the current draft; versions[1] is the one to roll back to.
    // Durable: the list comes from the DB, so this works after a reload.
    const previousVersion = versions[1] ?? null;
    if (!conversationId || !previousVersion) return;

    setError(null);
    setPreviewError(null);
    setRestoring(true);

    try {
      const payload = await invokeComposer({
        action: 'restore_version',
        conversationId,
        versionId: previousVersion.id,
      });
      applyResponse(payload);
      setSandboxTest(null);
      setMobilePane('preview');
      setForgePane(null);
    } catch (restoreError) {
      setPreviewError(
        toSafeUiMessage(
          restoreError instanceof Error ? restoreError.message : '',
          'Impossible de restaurer la version précédente.'
        )
      );
    } finally {
      setRestoring(false);
    }
  };

  // UX-only edit (section/tab labels + order): persist without re-running the
  // planner or the sandbox. The dominos/data are unchanged, so the result stays
  // valid — only the presentation changes.
  const handleValidateDesign = async () => {
    if (!conversationId) return;
    setError(null);
    setValidatingDesign(true);
    try {
      applyResponse(await invokeComposer({ action: 'validate_design', conversationId }));
    } catch (validationError) {
      setError(validationError instanceof Error ? validationError.message : 'La conception n’a pas pu être validée.');
    } finally {
      setValidatingDesign(false);
    }
  };

  const handleUiEdit = async (modifiedDraft: FlowComposerDraft) => {
    if (!conversationId) return;
    setError(null);
    setSavingLayout(true);
    try {
      const payload = await invokeComposer({
        action: 'ui_edit',
        conversationId,
        draft: modifiedDraft,
      });
      applyResponse(payload);
    } catch (uiEditError) {
      setError(
        uiEditError instanceof Error
          ? uiEditError.message
          : "Impossible d'appliquer la mise en forme"
      );
    } finally {
      setSavingLayout(false);
    }
  };

  // Bounded repair loop: send the technical hint to the planner, then auto
  // re-run the sandbox test on the repaired draft so the user immediately sees
  // whether it is fixed (build → test → repair → re-test).
  const handleRepair = async (instruction: string) => {
    const trimmed = instruction.trim();
    if (!conversationId || !trimmed) return;
    setError(null);
    setImproving(true);
    setStreamProgress(null);
    setSandboxTest(null);
    try {
      const payload = await streamComposer({
        action: 'answer',
        conversationId,
        message: trimmed,
        changeKind: 'repair',
      });
      applyResponse(payload);
      setMobilePane('preview');
      setForgePane(null);
      if (isPreviewResponse(payload)) {
        await handleTestDraft(payload.draft);
      }
    } catch (repairError) {
      setError(
        repairError instanceof Error
          ? repairError.message
          : 'Impossible de réparer le tool'
      );
    } finally {
      setImproving(false);
      setStreamProgress(null);
    }
  };

  const handleSandboxDiagnosisAction = async (action: ProductDiagnosisAction) => {
    if (action.type === 'connect_source') {
      navigate('/connections');
      return;
    }

    if (action.type === 'retry') {
      await handleTestDraft();
      return;
    }

    if (action.type === 'contact_support') {
      window.location.href = buildSupportMailto({
        subject: `Test de la Forge arrêté${draft?.title ? ` : ${draft.title}` : ''}`,
        executionId: sandboxTest?.executionId,
      });
      return;
    }

    if (action.type === 'open_run_details' && sandboxTest?.executionId) {
      // Open in a new tab so the Forge (and the in-progress test) stays mounted
      // — leaving the page used to drop the test run entirely.
      window.open(
        `/executions/${sandboxTest.executionId}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    // repair_tool / edit_tool / default → drive repair with the technical hint
    // (falls back to user copy for older runs without an internal diagnosis).
    const instruction =
      sandboxTest?.internalDiagnosis?.repairHint ??
      (sandboxTest?.diagnosis
        ? [sandboxTest.diagnosis.title, sandboxTest.diagnosis.explanation].join('\n')
        : null);
    if (!instruction) return;
    await handleRepair(instruction);
  };

  useEffect(() => {
    if (!requestedConversationId) return;
    if (requestedConversationId === conversationId && messages.length > 0) return;

    let canceled = false;
    resetComposerState(requestedConversationId);
    setLoading(true);
    setError(null);

    invokeComposer({
      action: 'resume',
      conversationId: requestedConversationId,
    })
      .then((payload) => {
        if (canceled) return;
        applyResponse(payload);
      })
      .catch((resumeError) => {
        if (canceled) return;
        setError(
          resumeError instanceof Error
            ? resumeError.message
            : 'Impossible de reprendre la conversation'
        );
        resetComposerState();
        syncConversationQuery(null);
      })
      .finally(() => {
        if (canceled) return;
        setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [requestedConversationId]);

  useEffect(() => {
    if (requestedConversationId || (!requestedFlowId && !requestedToolAppKey)) return;

    let canceled = false;
    resetComposerState();
    setLoading(true);
    setError(null);

    invokeComposer({
      action: requestedToolAppKey ? 'resume_tool_app' : 'resume_flow',
      ...(requestedToolAppKey ? { toolAppKey: requestedToolAppKey } : { flowId: requestedFlowId }),
    })
      .then((payload) => {
        if (canceled) return;
        applyResponse(payload);
      })
      .catch((resumeError) => {
        if (canceled) return;
        setError(
          resumeError instanceof Error
            ? resumeError.message
            : 'Impossible de reprendre le tool dans la Forge'
        );
      })
      .finally(() => {
        if (canceled) return;
        setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [requestedConversationId, requestedFlowId, requestedToolAppKey]);

  useEffect(() => {
    const initialPrompt = requestedInitialPrompt?.trim() ?? '';
    if (
      !initialPrompt ||
      requestedConversationId ||
      requestedFlowId ||
      conversationId ||
      messages.length > 0 ||
      startedPromptRef.current === initialPrompt
    ) {
      return;
    }

    // Not dropped when this effect re-runs: the turn itself changes the
    // messages and, once the server sends it, the conversation id. Only leaving
    // the page drops the result; startedPromptRef prevents a second start.
    startedPromptRef.current = initialPrompt;
    const optimisticMessage: FlowComposerMessage = {
      id: `initial-${Date.now()}`,
      role: 'user',
      content: initialPrompt,
      createdAt: new Date().toISOString(),
    };
    resetComposerState();
    setMessages([optimisticMessage]);
    setLoading(true);
    setStreamProgress(null);
    setError(null);

    streamComposer({
      action: 'start',
      prompt: initialPrompt,
    })
      .then((payload) => {
        if (!mountedRef.current) return;
        applyResponse(payload);
      })
      .catch((startError) => {
        if (!mountedRef.current) return;
        setMessages((current) =>
          current.filter((message) => message.id !== optimisticMessage.id)
        );
        setError(
          startError instanceof Error
            ? startError.message
            : 'Impossible d’initialiser la création d’outil'
        );
      })
      .finally(() => {
        if (mountedRef.current) {
          setLoading(false);
          setStreamProgress(null);
        }
      });
  }, [
    requestedConversationId,
    requestedFlowId,
    requestedInitialPrompt,
    conversationId,
    messages.length,
  ]);

  useEffect(() => {
    const repairPrompt = requestedRepairPrompt?.trim() ?? '';
    const repairKey =
      requestedRepairRunId && requestedFlowId
        ? `${requestedFlowId}:${requestedRepairRunId}`
        : null;
    if (
      !repairPrompt ||
      !repairKey ||
      !conversationId ||
      loading ||
      improving ||
      creating ||
      startedRepairRef.current === repairKey
    ) {
      return;
    }

    let canceled = false;
    startedRepairRef.current = repairKey;
    setImproving(true);
    setStreamProgress(null);
    setError(null);

    streamComposer({
      action: 'answer',
      conversationId,
      message: repairPrompt,
      changeKind: 'repair',
    })
      .then((payload) => {
        if (canceled) return;
        applyResponse(payload);
        setMobilePane('preview');
        setForgePane(null);
        // Close the loop: auto re-run the sandbox test on the repaired draft.
        if (isPreviewResponse(payload)) {
          void handleTestDraft(payload.draft);
        }
      })
      .catch((repairError) => {
        if (canceled) return;
        setError(
          repairError instanceof Error
            ? repairError.message
            : 'Impossible de lancer la réparation du tool'
        );
      })
      .finally(() => {
        if (!canceled) {
          setImproving(false);
          setStreamProgress(null);
        }
      });

    return () => {
      canceled = true;
    };
  }, [
    requestedRepairPrompt,
    requestedRepairRunId,
    requestedFlowId,
    conversationId,
    loading,
    improving,
    creating,
  ]);

  useEffect(() => {
    const executionId = sandboxTest?.executionId;
    if (
      !executionId ||
      sandboxTest.status === 'completed' ||
      sandboxTest.status === 'failed' ||
      sandboxTest.status === 'cancelled'
    ) {
      return;
    }

    let canceled = false;
    let timeoutId: number | null = null;
    let lastWatchdogAt = 0;

    const poll = async () => {
      const { data, error: executionError } = await supabase
        .from('flow_executions')
        .select(
          'id, status, updated_at, error, view_payload, result_payload, view_type_resolved, view_type_requested, execution_summary'
        )
        .eq('id', executionId)
        .single();

      if (canceled) return;
      if (executionError || !data) {
        timeoutId = window.setTimeout(poll, 2500);
        return;
      }

      const status = String((data as { status?: unknown }).status);
      const nextStatus = [
        'pending',
        'running',
        'paused',
        'completed',
        'failed',
        'cancelled',
      ].includes(status)
        ? (status as FlowExecutionStatus)
        : 'running';
      const row = data as {
        error: unknown;
        view_payload: unknown;
        result_payload: unknown;
        view_type_resolved: unknown;
        view_type_requested: unknown;
        execution_summary: unknown;
      };
      const diagnosis = extractProductDiagnosisFromError(row.error);
      const internalDiagnosis = extractInternalDiagnosisFromError(row.error);
      const resultPayload = parseResultPayload(row.result_payload);
      const viewPayload = row.view_payload;
      const fallback = isAnyViewEnvelope(viewPayload)
        ? {
            viewType: (isViewType(row.view_type_resolved)
              ? row.view_type_resolved
              : isViewType(row.view_type_requested)
                ? row.view_type_requested
                : viewPayload.view_type) as ViewType,
            envelope: viewPayload,
          }
        : null;
      const tabs =
        nextStatus === 'completed'
          ? buildRunResultTabs({
              title: draft?.title ?? 'Test',
              description:
                getExecutionSummaryNarration(row.execution_summary) ??
                draft?.description ??
                null,
              resultPayload,
              fallback,
              aggregateRuns: [],
            })
          : [];
      const hasBoundSections = Boolean(
        draft?.resultComposition?.tabs.some((tab) =>
          tab.sections.some((section) => section.source)
        )
      );
      const sandboxOutputUsable = isSandboxOutputUsable({
        hasBoundSections,
        resultPayloadStatus: resultPayload?.status,
        executionSummaryStatus: isRecord(row.execution_summary)
          ? row.execution_summary.result_status
          : undefined,
      });

      setSandboxTest((current) =>
        current?.executionId === executionId
          ? {
              ...current,
              status: nextStatus === 'completed' && !sandboxOutputUsable ? 'failed' : nextStatus,
              // Same rule as the publish gate, said as soon as the test ends.
              publishBlocked: nextStatus === 'completed'
                ? appDataIdentityProblem(draft?.appData, row.result_payload, row.view_payload)
                : null,
              objectCoverage: nextStatus === 'completed'
                ? appObjectCoverage(draft?.appData, row.result_payload, row.view_payload)
                : null,
              appTable: nextStatus === 'completed' ? appResultTable(row.result_payload, row.view_payload) ?? null : null,
              diagnosis,
              internalDiagnosis,
              resultTab: tabs[0] ?? current.resultTab ?? null,
              resultTabs: tabs,
            }
          : current
      );

      if (
        nextStatus === 'pending' ||
        nextStatus === 'running' ||
        nextStatus === 'paused'
      ) {
        // A run beats every 15 s while alive. Once silent, ask the server
        // watchdog to judge it, so a dead test ends in a failure the person
        // can act on instead of « Test en cours » forever (28/10/2026).
        const updatedAt = Date.parse(String((data as { updated_at?: unknown }).updated_at ?? ''));
        const now = Date.now();
        if (
          nextStatus !== 'paused' &&
          Number.isFinite(updatedAt) &&
          now - updatedAt > SANDBOX_SILENCE_MS &&
          now - lastWatchdogAt > SANDBOX_SILENCE_MS
        ) {
          lastWatchdogAt = now;
          void invokeExecutionChat({ action: 'resume', executionId }).catch((watchdogError: unknown) => {
            console.warn('sandbox_watchdog_failed', watchdogError);
          });
        }
        timeoutId = window.setTimeout(poll, 2500);
      }
    };

    void poll();

    return () => {
      canceled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [sandboxTest?.executionId, sandboxTest?.status, draft?.title, draft?.description]);

  useEffect(() => {
    if (requestedConversationId || requestedFlowId || requestedInitialPrompt) return;
    resetComposerState();
    startedPromptRef.current = null;
  }, [requestedConversationId, requestedFlowId, requestedInitialPrompt]);

  const pendingTurn = usePendingComposerTurn({
    conversationId,
    messages,
    serverTurn,
    busy: loading || creating || improving,
    fetchConversation: (id) => invokeComposer({ action: 'resume', conversationId: id }),
    applyPayload: (payload) => {
      setError(null);
      applyResponse(payload);
    },
  });

  const visibleForgeDrafts = forgeDrafts.filter(
    (item) => !item.flowId || !forgeTools.some((tool) => tool.id === item.flowId)
  );

  const forgeWorkspaceLists = shouldShowDraftRecovery ? (
    <div className="space-y-7">
      <ForgeToolAdminList
        tools={forgeTools}
        loading={forgeToolsLoading}
        error={forgeToolsError}
        onOpenTool={(flowId) => navigate(`/forge?flowId=${encodeURIComponent(flowId)}`)}
        onOpenApplication={(flowId) => navigate(`/tools/${flowId}`)}
        onActivateTool={(flowId) => void handleActivateForgeTool(flowId)}
        activatingToolId={activatingToolId}
        onRefresh={loadForgeTools}
      />
      {(forgeDraftsLoading || visibleForgeDrafts.length > 0 || forgeDraftsError) && (
        <ForgeDraftRecoveryList
          drafts={visibleForgeDrafts}
          loading={forgeDraftsLoading}
          error={forgeDraftsError}
          onOpenDraft={handleOpenForgeDraft}
          onRefresh={loadForgeDrafts}
        />
      )}
    </div>
  ) : null;

  const chatBusy = loading || pendingTurn === 'pending';
  const latestVersionNumber = versions.reduce<number | null>(
    (latest, version) =>
      latest === null || version.versionNumber > latest ? version.versionNumber : latest,
    null
  );

  return (
    <div className="h-[calc(100vh-1rem)] bg-[linear-gradient(180deg,#FDFBF7_0%,#FAF7F0_100%)] p-3 lg:p-4">
      <div className="mb-3 flex gap-2 lg:hidden">
        <Button
          variant={mobilePane === 'chat' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setMobilePane('chat')}
        >
          Chat
        </Button>
        <Button
          variant={mobilePane === 'preview' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setMobilePane('preview')}
        >
          {hasPreview ? 'Aperçu' : 'Forge'}
        </Button>
      </div>

      {/* The chat always stays on the left; the right pane shows the app, or
          where it will appear. */}
      <div className="grid h-full overflow-hidden rounded-[28px] border border-domino-cardBorder bg-domino-white shadow-[var(--domino-shadow-soft)] lg:grid-cols-[420px_minmax(0,1fr)]">
        <section
          className={cn(
            'flex min-h-0 flex-col bg-domino-white lg:border-r lg:border-domino-cardBorder',
            mobilePane === 'preview' ? 'hidden lg:flex' : 'flex'
          )}
        >
          {error && (
            <div role="alert" className="px-6 pt-4 text-sm text-domino-inkSoft">
              {toSafeUiMessage(
                error,
                'Domino ne peut pas ouvrir ce brouillon pour le moment.'
              )}
            </div>
          )}

          {pendingTurn === 'interrupted' && (
            <div role="status" className="px-6 pt-4 text-sm text-domino-inkSoft">
              La réponse à votre dernier message a été interrompue. Renvoyez-le pour relancer.
            </div>
          )}

          <ComposerChat
            messages={messages}
            loading={chatBusy}
            disabled={creating || improving}
            placeholder={chatPlaceholder}
            showInitialState={showInitialState}
            progress={
              pendingTurn === 'pending'
                ? { stage: 'reasoning', label: 'Domino travaille encore sur votre dernier message' }
                : streamProgress
            }
            onSend={handleSend}
            draftRequest={chatDraftRequest}
            onStop={conversationId ? () => void handleStop() : undefined}
            stopping={stopping}
            liveChecks={liveChecks}
          />
        </section>

        <section
          className={cn(
            'min-h-0 overflow-y-auto bg-[linear-gradient(180deg,#FAF7F0_0%,#FDFBF7_100%)]',
            mobilePane === 'chat' ? 'hidden lg:block' : 'block'
          )}
        >
          {!hasPreview && (usageSheetDraft ?? usageSheet) ? (
            <div className="mx-auto w-full max-w-3xl px-5 py-6 lg:px-8">
              <UsageSheetCard
                sheet={(usageSheetDraft ?? usageSheet)!}
                validated={false}
                toolBuilt={false}
                building={chatBusy}
                writing={Boolean(usageSheetDraft)}
                onValidate={handleValidateSheet}
                onChange={handleEditSheet}
              />
              {sheetPreviewPanel && <div className="mt-6">{sheetPreviewPanel}</div>}
            </div>
          ) : null}
          {!hasPreview && !usageSheet && !usageSheetDraft ? (
            <ForgeStage isInitial={showInitialState} working={chatBusy}>
              {forgeWorkspaceLists}
            </ForgeStage>
          ) : null}
          {hasPreview && draft && resources ? (
            <PreviewVersionFrame
              revising={chatBusy || improving}
              versionKey={draftSignature}
              versionNumber={latestVersionNumber}
            >
              <ComposerPreview
                draft={draft}
                resources={resources}
                forgePane={forgePane}
                availableFolders={availableFolders}
                selectedKnowledgeFolderIds={extraKnowledgeFolderIds}
                creating={creating}
                improving={improving}
                testing={testing}
                restoring={restoring}
                canRestorePreviousDraft={versions.length > 1}
                sandboxTest={sandboxTest}
                confirmLabel={requestedFlowId ? 'Enregistrer' : 'Créer l’outil'}
                onForgePaneChange={setForgePane}
                onToggleKnowledgeFolder={(folderId, nextSelected) => {
                  setExtraKnowledgeFolderIds((current) => {
                    if (nextSelected) {
                      return current.includes(folderId)
                        ? current
                        : [...current, folderId];
                    }
                    return current.filter((id) => id !== folderId);
                  });
                }}
                onTest={() => handleTestDraft()}
                onProvideTestInputs={(values) => handleTestDraft(undefined, values)}
                onUiEdit={handleUiEdit}
                onValidateDesign={() => void handleValidateDesign()}
                appSlot={forgeApp ? <ForgeAppPages app={forgeApp} /> : undefined}
                sheetSlot={(usageSheetDraft ?? usageSheet) ? (
                  <UsageSheetCard
                    key={usageSheetValidated ? 'validated' : 'changed'}
                    sheet={(usageSheetDraft ?? usageSheet)!}
                    validated={usageSheetValidated}
                    toolBuilt
                    alwaysOpen
                    building={chatBusy}
                    writing={Boolean(usageSheetDraft)}
                    onValidate={handleValidateSheet}
                    onChange={handleEditSheet}
                  />
                ) : undefined}
                sheetBlocksTest={Boolean(usageSheet && !usageSheetValidated)}
                onValidateSheet={handleValidateSheet}
                busy={chatBusy}
                actionError={previewError}
                exampleSlot={sheetPreviewPanel ?? undefined}
                appPreview={appPreview}
                validatingDesign={validatingDesign}
                savingLayout={savingLayout}
                onConfirm={handleConfirm}
                onRestorePreviousDraft={handleRestorePreviousDraft}
                onSandboxAction={handleSandboxDiagnosisAction}
                onDraftMessage={handleDraftMessage}
              />
            </PreviewVersionFrame>
          ) : null}
        </section>
      </div>
    </div>
  );
};
